/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { StoreConfigModel } from '@/cart/data/models';

export function transformStoreConfig(data: any): StoreConfigModel | null {
  if (!data) return null;

  const transformTaxDisplaySetting = (taxDisplaySetting: number) => {
    switch (taxDisplaySetting) {
      case 1:
        return 'EXCLUDING_TAX';
      case 2:
        return 'INCLUDING_TAX';
      case 3:
        return 'INCLUDING_EXCLUDING_TAX';
      default:
        return 'EXCLUDING_TAX';
    }
  };

  // A `null` value indicates the "use system value" option, which requires special handling
  // based on both global and product-level configurations
  const transformGiftOptionsCode = (data: string) => {
    switch (+data) {
      case 0:
        return false;
      case 1:
        return true;
      case 2:
        return null;
      default:
        return false;
    }
  };

  return {
    displayMiniCart: data.minicart_display,
    miniCartMaxItemsDisplay: data.minicart_max_items,
    cartExpiresInDays: data.cart_expires_in_days,
    cartSummaryDisplayTotal: data.cart_summary_display_quantity,
    cartSummaryMaxItems: data.max_items_in_order_summary,
    defaultCountry: data.default_country!,
    categoryFixedProductTaxDisplaySetting:
      data.category_fixed_product_tax_display_setting,
    productFixedProductTaxDisplaySetting:
      data.product_fixed_product_tax_display_setting,
    salesFixedProductTaxDisplaySetting:
      data.sales_fixed_product_tax_display_setting,
    shoppingCartDisplaySetting: {
      zeroTax: data.shopping_cart_display_zero_tax,
      subtotal: transformTaxDisplaySetting(data.shopping_cart_display_subtotal),
      price: transformTaxDisplaySetting(data.shopping_cart_display_price),
      shipping: transformTaxDisplaySetting(data.shopping_cart_display_shipping),
      fullSummary: data.shopping_cart_display_full_summary,
      grandTotal: data.shopping_cart_display_grand_total,
      taxGiftWrapping: data.shopping_cart_display_tax_gift_wrapping,
    },
    useConfigurableParentThumbnail:
      data.configurable_thumbnail_source === 'parent',
    allowGiftWrappingOnOrder: transformGiftOptionsCode(
      data?.allow_gift_wrapping_on_order
    ),
    allowGiftWrappingOnOrderItems: transformGiftOptionsCode(
      data?.allow_gift_wrapping_on_order_items
    ),
    allowGiftMessageOnOrder: transformGiftOptionsCode(data?.allow_order),
    allowGiftMessageOnOrderItems: transformGiftOptionsCode(data?.allow_items),
    allowGiftReceipt: Boolean(+data?.allow_gift_receipt),
    allowPrintedCard: Boolean(+data?.allow_printed_card),
    printedCardPrice: {
      currency: data?.printed_card_priceV2?.currency ?? '',
      value:
        data?.printed_card_priceV2?.value != null
          ? +data.printed_card_priceV2.value
          : 0,
    },
    cartGiftWrapping: transformTaxDisplaySetting(+data.cart_gift_wrapping),
    cartPrintedCard: transformTaxDisplaySetting(+data.cart_printed_card),
  };
}
